import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { MessageCircle, Loader2, ShieldAlert } from "lucide-react";

export default function PublicRoute() {
  const [config, setConfig] = useState(null);
  const [error, setError] = useState("");
  const started = useRef(false);
  const source =
    new URLSearchParams(window.location.search).get("utm_source") ||
    new URLSearchParams(window.location.search).get("fuente") ||
    "anuncio";

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    // Bot/automation guard: headless drivers never advance the round-robin.
    if (typeof navigator !== "undefined" && navigator.webdriver) {
      setConfig({ business_name: "Envolvía Global", has_available: false, has_backup: false });
      return;
    }

    let timer;
    api
      .get("/public/config")
      .then(({ data }) => {
        setConfig(data);
        if (!data.has_available && !data.has_backup) return;
        timer = setTimeout(async () => {
          try {
            const { data: r } = await api.post("/public/route", { source });
            window.location.href = r.wa_url;
          } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
          }
        }, 1000);
      })
      .catch(() => setConfig({ business_name: "Envolvía Global", has_available: false, has_backup: false }));

    return () => timer && clearTimeout(timer);
  }, [source]);

  const business = config?.business_name || "Envolvía Global";
  const noOne = config && !config.has_available && !config.has_backup;

  return (
    <div className="min-h-screen bg-[#0B0F14] flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-[#25D366]/10 via-transparent to-transparent" />
      <div className="w-full max-w-md relative z-10 text-center">
        <div className="w-20 h-20 rounded-3xl bg-[#25D366] flex items-center justify-center mx-auto mb-6 shadow-[0_0_40px_rgba(37,211,102,0.4)]">
          <MessageCircle className="w-11 h-11 text-[#0B0F14]" strokeWidth={2.5} />
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-50 mb-2">{business}</h1>

        {noOne ? (
          <div data-testid="public-no-availability" className="bg-[#2A181A] border border-red-500/30 rounded-2xl p-6 mt-6">
            <ShieldAlert className="w-10 h-10 text-red-400 mx-auto mb-3" />
            <p className="text-slate-200">Todos nuestros asesores están ocupados. Intenta de nuevo en unos minutos.</p>
          </div>
        ) : error ? (
          <div data-testid="public-error" className="bg-[#2A181A] border border-red-500/30 rounded-2xl p-6 mt-6">
            <ShieldAlert className="w-10 h-10 text-red-400 mx-auto mb-3" />
            <p className="text-slate-200">{error}</p>
          </div>
        ) : (
          <div data-testid="public-connecting" className="mt-8 flex flex-col items-center">
            <Loader2 className="w-10 h-10 text-[#25D366] animate-spin mb-4" />
            <p className="text-slate-300 text-lg">Conectando con un asesor…</p>
            <p className="text-slate-500 text-sm mt-1">Abriendo WhatsApp en un momento.</p>
          </div>
        )}

        <p className="text-xs text-slate-600 mt-8 font-mono-lf">Powered by LeadFlow.WA</p>
      </div>
    </div>
  );
}
